import React, { useState } from 'react';
import { useAdmin } from './AdminContext';
import { Lock, ArrowRight, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const LOGO_URL = '/couplein_logo.png';

export default function AdminLogin() {
  const { login, authLoading, authError, setAuthError } = useAdmin();
  const [passcode, setPasscode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    await login(passcode);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-pink-950 flex items-center justify-center p-4 selection:bg-pink-500 selection:text-white">
      {/* Ambient background blur circles */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pink-500/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md bg-white/95 backdrop-blur-xl rounded-3xl p-8 sm:p-10 shadow-2xl border border-white/20">
        {/* Brand Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 text-white shadow-lg shadow-pink-500/25 mb-4">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
            CoupleIn Admin
          </h1>
          <p className="text-sm text-gray-500 mt-1.5">
            Enter your secret administrative passcode to access CMS and visitor metrics.
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
              Passcode
            </label>
            <div className="relative">
              <Input
                type="password"
                value={passcode}
                onChange={(e) => {
                  setPasscode(e.target.value);
                  if (authError) setAuthError('');
                }}
                placeholder="••••••••••••"
                className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl text-gray-900 text-base focus:bg-white focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                autoFocus
                disabled={authLoading}
              />
            </div>
            {authError && (
              <p className="text-xs text-rose-600 font-semibold mt-2 flex items-center gap-1">
                <span>⚠️</span> {authError}
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={authLoading || !passcode}
            className="w-full bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white font-semibold py-3.5 rounded-xl shadow-md shadow-pink-500/20 flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            {authLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <span>Access Dashboard</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </form>

        {/* Security Notice */}
        <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            End-to-end verified
          </span>
          <a href="/" className="hover:text-gray-600 transition-colors">
            Return to site →
          </a>
        </div>
      </div>
    </div>
  );
}
